// Un estado de cuenta: el total, lo depositado por vendedor y cada depósito, con su CSV.
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import type { RetailCommission } from '@fiado/type-kit';
import { userMessageOf } from '@/api/errors';
import { Button, Skeleton } from '@/kit';
import { formatDateMx, formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { useExportStatement, useStatement } from '../../api/commissionQueries';
import { downloadEngineCsv } from '../../lib/engineCsv';
import { BILLING_PERIOD_LABEL } from '../../lib/ruleText';
import { statementBySeller, statementPeriodText } from '../../lib/statementRows';
import { SECTION_TITLE } from '../fieldStyles';

type Payment = RetailCommission.CommissionPayment;

interface StatementDetailProps {
    statement: RetailCommission.CommissionChainStatement;
    /** `null`: la cadena del token. */
    retailerId: string | null;
    sellerName: (sellerId: string) => string;
}

export function StatementDetail({ statement, retailerId, sellerName }: StatementDetailProps) {
    const detail = useStatement(statement.statementId, retailerId);
    const exporter = useExportStatement();
    const payments = detail.data?.payments ?? [];
    const bySeller = statementBySeller(payments, sellerName);

    const download = () => {
        exporter.mutate(
            { statementId: statement.statementId, retailerId },
            {
                onSuccess: downloadEngineCsv,
                onError: (error) => {
                    toast.error(userMessageOf(error));
                },
            },
        );
    };

    const columns: DataTableColumn<Payment>[] = [
        {
            key: 'when',
            label: 'Confirmado',
            width: '124px',
            cell: (p) => formatDateTimeMx(p.resolvedAt ?? p.requestedAt),
        },
        { key: 'seller', label: 'Vendedor', cell: (p) => sellerName(p.sellerId) },
        { key: 'folio', label: 'Folio', width: '110px', cell: (p) => p.transactionId ?? '—' },
        {
            key: 'amount',
            label: 'Monto',
            width: '100px',
            numeric: true,
            cell: (p) => formatMoneyCents(p.amountCents),
        },
    ];

    return (
        <div className="flex h-full min-h-0 flex-col overflow-auto">
            <header className="flex flex-none items-start gap-2 border-b border-border px-4 py-3">
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                        Cobro {BILLING_PERIOD_LABEL[statement.period].toLowerCase()} · generado{' '}
                        {formatDateMx(statement.generatedAt)}
                    </p>
                    <h2 className="truncate text-[15px] font-semibold">
                        {statementPeriodText(statement)}
                    </h2>
                    <p className="text-[24px] font-semibold tabular-nums">
                        {formatMoneyCents(statement.accreditedCents)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                        {statement.paymentsCount} depósitos confirmados · {statement.sellersCount}{' '}
                        vendedores. Lo pagado por fuera no se cobra.
                    </p>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Download className="size-3.5" aria-hidden />}
                    isLoading={exporter.isPending}
                    onClick={download}
                >
                    CSV
                </Button>
            </header>
            <section className="border-b border-border px-4 py-3">
                <h3 className={SECTION_TITLE}>Por vendedor</h3>
                {detail.isLoading ? (
                    <Skeleton lines={3} />
                ) : detail.isError ? (
                    <p className="text-[11px] text-destructive">
                        No se pudo leer el detalle del estado de cuenta.
                    </p>
                ) : (
                    <ul
                        aria-label="Depositado por vendedor"
                        className="flex flex-col gap-1 text-[11.5px]"
                    >
                        {bySeller.map((row) => (
                            <li key={row.sellerId} className="flex items-center gap-2">
                                <span className="min-w-0 flex-1 truncate">{row.sellerName}</span>
                                <span className="text-muted-foreground">
                                    {row.paymentsCount} dep.
                                </span>
                                <span className="w-[96px] text-right font-semibold tabular-nums">
                                    {formatMoneyCents(row.amountCents)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
            <section className="flex min-h-0 flex-col px-4 py-3">
                <h3 className={SECTION_TITLE}>Depósitos</h3>
                <DataTable
                    columns={columns}
                    rows={detail.isError ? null : payments}
                    rowKey={(p) => p.paymentId}
                    caption="Depósitos del estado de cuenta"
                    isLoading={detail.isLoading}
                    empty="Sin depósitos en este periodo."
                    unavailable={null}
                    density="compact"
                />
            </section>
        </div>
    );
}
